import { FC, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  Box,
  Typography,
} from '@mui/material'
import { IPlaybookReport } from '../../../../../shared/types/api/playbook.type'

interface IProps {
  open: boolean
  report: IPlaybookReport | null
  onClose: () => void
  onSave: (body: { name: string; slug: string; query: string; params: string[] }) => void
}

const PlaybookReportModal: FC<IProps> = ({ open, report, onClose, onSave }) => {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [query, setQuery] = useState('')
  const [params, setParams] = useState<string[]>([])
  const [paramInput, setParamInput] = useState('')

  useEffect(() => {
    if (report) {
      setName(report.name)
      setSlug(report.slug)
      setQuery(report.query)
      setParams(report.params ?? [])
    } else {
      setName('')
      setSlug('')
      setQuery('')
      setParams([])
    }
    setParamInput('')
  }, [report, open])

  const handleSave = () => {
    onSave({ name, slug, query, params })
  }

  const handleAddParam = () => {
    const trimmed = paramInput.trim()
    if (trimmed && !params.includes(trimmed)) {
      setParams((prev) => [...prev, trimmed])
    }
    setParamInput('')
  }

  const handleParamKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddParam()
    }
  }

  const handleDeleteParam = (param: string) => {
    setParams((prev) => prev.filter((p) => p !== param))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{report ? 'Edit Report' : 'Add Report'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          data-testid="report-name"
          inputProps={{ 'data-testid': 'report-name-input' }}
        />
        <TextField
          label="Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          fullWidth
          size="small"
          data-testid="report-slug"
          inputProps={{ 'data-testid': 'report-slug-input' }}
        />
        <TextField
          label="Query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          size="small"
          multiline
          rows={4}
          data-testid="report-query"
          inputProps={{ 'data-testid': 'report-query-input' }}
        />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Parameters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              label="Add parameter"
              value={paramInput}
              onChange={(e) => setParamInput(e.target.value)}
              onKeyDown={handleParamKeyDown}
              size="small"
              fullWidth
              data-testid="report-param"
              inputProps={{ 'data-testid': 'report-param-input' }}
            />
            <Button onClick={handleAddParam} variant="outlined" size="small" data-testid="report-add-param-button">
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {params.map((param) => (
              <Chip
                key={param}
                label={param}
                onDelete={() => handleDeleteParam(param)}
                size="small"
                data-testid={`report-param-chip-${param}`}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="report-cancel-button">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" data-testid="report-save-button">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PlaybookReportModal
